<template>
    <template v-if="isLoading">
        <v-skeleton-loader type="card" class="mx-auto"></v-skeleton-loader>
    </template>
    <template v-else>
        <card-skeleton v-for="(card, index) in visibleCards"
                       :key="card.id"
                       :class="cardClass(visibleCards, index, !showBlocked)">
            <v-card-text class="text-size-md pt-0">
                <travel-card-item v-bind="card" :card-type="cardType" />
            </v-card-text>
            <div v-if="index === visibleCards.length - 1">
                <v-card-text v-if="blockedCards.length && !showBlocked" class="text-size-md pt-0">
                    <load-more-button data-id="travelCardLoadMoreButton"
                                      :disabled="isMoreLoading || !blockedCards.length"
                                      :loading="isMoreLoading"
                                      @click="handleLoadMore"
                                      :text="blockedButtonText" />
                </v-card-text>
            </div>

            <v-divider v-if="index < visibleCards.length - 1 || (showBlocked && blockedCardsShown.length > 0 && index === visibleCards.length - 1)"
                       :thickness="2"
                       class="mt-4"></v-divider>
        </card-skeleton>
        <template v-if="!visibleCards.length && blockedCards.length && !showBlocked">
            <card-skeleton :class="['rounded-lg','mb-6','px-2','py-2','single-travel-card']" key="blocked-placeholder">
                <v-card-text class="text-size-md">
                    <div class="text-center ma-0">
                        <load-more-button data-id="travelCardLoadMoreButton"
                                          :disabled="isMoreLoading || !blockedCards.length"
                                          :loading="isMoreLoading"
                                          @click="handleLoadMore"
                                          :text="blockedButtonText" />
                    </div>
                </v-card-text>
            </card-skeleton>
        </template>
        <card-skeleton v-for="(card, index) in blockedCardsShown"
                       :key="card.id"
                       :class="cardClass(blockedCardsShown, index, true)">
            <v-card-text class="text-size-md pt-0">
                <travel-card-item v-bind="card" :card-type="cardType" />
            </v-card-text>
            <v-divider v-if="index < blockedCardsShown.length - 1"
                       :thickness="2"
                       class="mt-4"></v-divider>


            <v-card-text v-if="index === blockedCardsShown.length - 1" class="text-center ma-0 blocked-load-more-container">
                <load-more-button data-id="travelCardBlockedLoadMoreButton"
                                  class="blocked-load-more"
                                  :disabled="isMoreLoading || !blockedCards.length"
                                  :loading="isMoreLoading"
                                  @click="handleLoadMore"
                                  :text="blockedButtonText" />
            </v-card-text>
        </card-skeleton>

    </template>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { ITravelCardList, TravelCardDto } from '../../types';
import TravelCardItem from '../card/TravelCardItem.vue';
import CardSkeleton from '@/features/common/components/CardSkeleton.vue';
import LoadMoreButton from '@/features/common/components/LoadMoreButton.vue';
import { TravelCardStatus } from '../../constants';

const props = defineProps<ITravelCardList>();
const showBlocked = ref(false);
const isMoreLoading = ref(false);

const BLOCKED_BATCH_SIZE = 3;
const blockedPage = ref(0);

const visibleCards = computed(() => {
    return (props.travelCards || []).filter((c: TravelCardDto) => c.status !== TravelCardStatus.Blocked);
});

const blockedCards = computed(() => {
    return (props.travelCards || []).filter((c: TravelCardDto) => c.status === TravelCardStatus.Blocked);
});

const blockedTotal = computed(() => blockedCards.value.length);
const blockedShownCount = computed(() => {
    return blockedPage.value > 0 ? Math.min(blockedPage.value * BLOCKED_BATCH_SIZE, blockedTotal.value) : 0;
});

const blockedMaxReached = computed(() => blockedShownCount.value >= blockedTotal.value);

const blockedCardsShown = computed(() => {
    if (!showBlocked.value) return [];
    return blockedCards.value.slice(0, blockedShownCount.value || BLOCKED_BATCH_SIZE);
});

const blockedButtonText = computed(() =>
  showBlocked.value && blockedMaxReached.value
    ? 'startPage.showLess'
    : 'startPage.showBlockedCards'
);

const handleLoadMore = () => {
    if (!blockedTotal.value) return;

    if (showBlocked.value && blockedMaxReached.value) {
        showBlocked.value = false;
        blockedPage.value = 0;
        return;
    }

    if (!showBlocked.value) {
        showBlocked.value = true;
        blockedPage.value = 1;
        return;
    }

    if (blockedMaxReached.value) return;

    isMoreLoading.value = true;
    blockedPage.value += 1;
    isMoreLoading.value = false;
};

function cardClass(items: TravelCardDto[], index: number, isLastGroup = true) {
    const length = (items || []).length;
    const base = ['px-2', 'py-2', 'single-travel-card'];

    if (length === 0) return base;

    if (index === 0 && index === length - 1) {
        if (isLastGroup) {
            return ['rounded-lg', 'mb-6', ...base];
        }
        return base;
    }

    if (index === 0) {
        return ['rounded-t-lg', ...base];
    }

    if (index === length - 1) {
        if (isLastGroup) {
            return ['rounded-b-lg', 'mb-6', ...base];
        }
        return base;
    }
    return base;
}
</script>
